import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Input } from 'reactstrap';
import { connect } from 'react-redux';
import debounce from 'lodash.debounce';
import { VirtuosoGrid } from 'react-virtuoso';
import { Edit2, Trash2 } from 'react-feather';
import {
  getSalesAgentProfiles as getSalesAgentProfilesAction,
  deleteSalesAgentConnectedProfiles as deleteSalesAgentConnectedProfilesAction,
} from '../../ReduxToolkit/Reducers/organizationManagement';
import SalesAgentProfileCard from './SalesAgentProfileCard';
import ProfileInfoLoader from '../Profile/ProfileInfoTab/ProfileInfoSection/ProfileInfoLoader';
import ConfirmationModal from '../ConfirmationModal';
import AddSalesAgentProfilesModal from './AddSalesAgentProfilesModal';

const TAKE = 40;

const GridList = React.forwardRef(({ style, children, ...props }, ref) => (
  <div
    ref={ref}
    {...props}
    style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(max(260px, calc(25% - 1rem)), 1fr))',
      gap: '1rem',
      ...style,
    }}
  >
    {children}
  </div>
));
GridList.displayName = 'GridList';

const GridItem = ({ children, ...props }) => <div {...props}>{children}</div>;

const SalesAgentProfilesSection = ({
  organizationId,
  profileType,
  profileItems,
  totalProfiles,
  isLoading,
  getSalesAgentProfiles,
  deleteSalesAgentConnectedProfiles,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedProfileIds, setSelectedProfileIds] = useState([]);
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);
  const pageRef = useRef(1);

  const currentProfiles = profileItems[profileType] || [];
  const total = totalProfiles[profileType] || 0;

  const refreshProfiles = useCallback(
    async (append = false) => {
      if (!organizationId) return;
      const page = append ? pageRef.current : 1;
      if (!append) pageRef.current = 1;
      await getSalesAgentProfiles(
        organizationId,
        profileType,
        page,
        TAKE,
        debouncedQuery,
        append
      );
    },
    [organizationId, profileType, debouncedQuery, getSalesAgentProfiles]
  );

  useEffect(() => {
    setSelectedProfileIds([]);
    setSearchTerm('');
    setDebouncedQuery('');
    pageRef.current = 1;
  }, [profileType, organizationId]);

  useEffect(() => {
    pageRef.current = 1;
    refreshProfiles(false);
  }, [profileType, organizationId, debouncedQuery, refreshProfiles]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceSearch = useCallback(
    debounce((value) => setDebouncedQuery(value.trim()), 800),
    []
  );

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    debounceSearch(e.target.value);
  };

  const loadMore = useCallback(() => {
    if (isLoading || currentProfiles.length >= total) return;
    pageRef.current += 1;
    refreshProfiles(true);
  }, [isLoading, currentProfiles.length, total, refreshProfiles]);

  const handleToggleSelect = (profileId) => {
    setSelectedProfileIds((prev) =>
      prev.includes(profileId)
        ? prev.filter((id) => id !== profileId)
        : [...prev, profileId]
    );
  };

  const handleRemoveProfiles = async () => {
    if (!selectedProfileIds.length) return;

    try {
      setRemoveLoading(true);
      const result = await deleteSalesAgentConnectedProfiles(
        organizationId,
        selectedProfileIds,
        profileType
      );

      if (result?.statusCode === 200) {
        setSelectedProfileIds([]);
        setRemoveModalOpen(false);
        pageRef.current = 1;
        await getSalesAgentProfiles(
          organizationId,
          profileType,
          1,
          TAKE,
          debouncedQuery,
          false
        );
      }
    } finally {
      setRemoveLoading(false);
    }
  };

  const handleAddSuccess = async () => {
    pageRef.current = 1;
    await getSalesAgentProfiles(
      organizationId,
      profileType,
      1,
      TAKE,
      debouncedQuery,
      false
    );
  };

  const GridFooter = useCallback(
    () =>
      isLoading ? (
        <div className="d-flex justify-content-center py-3">
          <div className="spinner-border spinner-border-sm text-secondary" role="status" />
        </div>
      ) : currentProfiles.length < total ? (
        <div className="d-flex justify-content-center py-2">
          <small className="text-muted">Scroll to load more</small>
        </div>
      ) : null,
    [isLoading, currentProfiles.length, total]
  );

  return (
    <Card className="p-4 shadow-sm rounded border-0">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-3">
        <div className="d-flex flex-column flex-md-row align-items-md-center gap-3">
          <button
            type="button"
            className="btn btn-outline-primary d-flex align-items-center gap-2"
            onClick={() => setAddModalOpen(true)}
            disabled={isLoading}
          >
            <Edit2 size={18} />
            Add {profileType === 'brand' ? 'Brands' : 'Retailers'}
          </button>
          <p className="mb-0">
            Total: <span className="fw-bold">{total}</span> profiles
          </p>
        </div>

        <div className="d-flex align-items-center gap-2">
          {selectedProfileIds.length > 0 && (
            <Button
              color="danger"
              outline
              size="sm"
              className="d-flex align-items-center gap-2"
              onClick={() => setRemoveModalOpen(true)}
            >
              <Trash2 size={16} />
              Remove ({selectedProfileIds.length})
            </Button>
          )}
          <Input
            type="text"
            placeholder="Search by name, id, or website"
            value={searchTerm}
            onChange={handleSearchChange}
            style={{ minWidth: '280px' }}
          />
        </div>
      </div>

      {isLoading && currentProfiles.length === 0 ? (
        <ProfileInfoLoader />
      ) : currentProfiles.length > 0 ? (
        <VirtuosoGrid
          style={{ height: '600px' }}
          totalCount={currentProfiles.length}
          endReached={loadMore}
          components={{
            List: GridList,
            Item: GridItem,
            Footer: GridFooter,
          }}
          itemContent={(index) => {
            const profile = currentProfiles[index];
            const profileId = profile?.brand?.id || profile?.retailer?.id;
            return (
              <SalesAgentProfileCard
                profile={profile}
                profileType={profileType}
                isSelected={selectedProfileIds.includes(profileId)}
                onToggleSelect={handleToggleSelect}
              />
            );
          }}
        />
      ) : (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: '300px' }}
        >
          <h5 className="text-muted mb-0">No {profileType} profiles connected yet</h5>
        </div>
      )}

      <ConfirmationModal
        open={removeModalOpen}
        onClose={() => setRemoveModalOpen(false)}
        onSubmit={handleRemoveProfiles}
        isLoading={removeLoading}
        title="Remove Connected Profiles"
        confirmButtonText="Remove"
        message={`Are you sure you want to remove ${selectedProfileIds.length} selected ${profileType} profile(s) from this sales agent organization?`}
      />

      <AddSalesAgentProfilesModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        organizationId={organizationId}
        profileType={profileType}
        onSuccess={handleAddSuccess}
      />
    </Card>
  );
};

const mapStateToProps = (state) => ({
  profileItems: state.organizationManagement.salesAgentProfileItems || { brand: [], retailer: [] },
  totalProfiles: state.organizationManagement.totalSalesAgentProfiles || {},
  isLoading: state.organizationManagement.salesAgentProfilesLoading,
});

const mapDispatchToProps = {
  getSalesAgentProfiles: getSalesAgentProfilesAction,
  deleteSalesAgentConnectedProfiles: deleteSalesAgentConnectedProfilesAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(SalesAgentProfilesSection);
